import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Car, Barcode, Loader2, AlertCircle, CheckCircle, Save, ChevronRight, Package } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../App";
import ProductCard from "../components/ProductCard";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const VINSearchPage = () => {
  const { isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  const [vin, setVin] = useState("");
  const [loading, setLoading] = useState(false);
  const [vehicle, setVehicle] = useState(null);
  const [products, setProducts] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");

  const categories = [
    { id: "", name: "All Categories" },
    { id: "brakes", name: "Brakes" },
    { id: "suspension", name: "Suspension" },
    { id: "ignition", name: "Ignition" },
    { id: "lighting", name: "Lighting" },
    { id: "multimedia", name: "Multimedia" }
  ];

  const handleVINLookup = async (e) => {
    e.preventDefault();
    setError("");
    setVehicle(null);
    setProducts([]);
    
    const cleanVin = vin.toUpperCase().trim();
    
    if (cleanVin.length !== 17) {
      setError("VIN must be exactly 17 characters");
      return;
    }
    
    if (/[IOQ]/.test(cleanVin)) {
      setError("VIN cannot contain letters I, O, or Q");
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/vin/search-parts`, 
        { vin: cleanVin },
        { params: { category: selectedCategory || undefined } }
      );
      
      const data = response.data;
      
      if (!data.vehicle || !data.vehicle.year) {
        setError("Could not decode VIN. Please verify it's correct.");
        return;
      }
      
      setVehicle(data.vehicle);
      setProducts(data.products || []);
      toast.success(`Found ${data.total_found} compatible parts for ${data.vehicle.year} ${data.vehicle.make} ${data.vehicle.model}`);
    } catch (err) {
      console.error("VIN lookup error:", err);
      setError(err.response?.data?.detail || "Failed to decode VIN. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVehicle = async () => {
    if (!isAuthenticated) {
      toast.error("Please login to save vehicles");
      navigate("/auth");
      return;
    }
    
    setSaving(true);
    try {
      await axios.post(
        `${API}/garage/vehicles`,
        null,
        {
          params: { vin: vehicle.vin },
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );
      toast.success("Vehicle saved to your garage!");
    } catch (err) {
      if (err.response?.data?.detail === "Vehicle already saved") {
        toast.info("This vehicle is already in your garage");
      } else {
        toast.error(err.response?.data?.detail || "Failed to save vehicle");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCategoryChange = async (cat) => {
    setSelectedCategory(cat);
    if (vehicle) {
      setLoading(true);
      try {
        const response = await axios.post(`${API}/vin/search-parts`, 
          { vin: vehicle.vin },
          { params: { category: cat || undefined } }
        );
        setProducts(response.data.products || []);
      } catch (err) {
        console.error("Filter error:", err);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 animate-fade-in" data-testid="vin-search-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 py-12">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-primary mb-2">
              <Barcode className="w-5 h-5" />
              <span className="font-medium uppercase tracking-wider text-sm">VIN Lookup</span>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-gray-900 uppercase tracking-tight">
              Find Parts By VIN
            </h1>
            <p className="text-gray-600 mt-3 text-lg">
              Enter your 17-character Vehicle Identification Number to instantly find compatible parts
            </p>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* VIN Search Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white border border-gray-200 rounded-lg p-6 sticky top-32">
              <form onSubmit={handleVINLookup} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vehicle Identification Number (VIN)
                  </label>
                  <Input
                    type="text"
                    placeholder="Enter 17-character VIN"
                    value={vin}
                    onChange={(e) => setVin(e.target.value.toUpperCase())}
                    maxLength={17}
                    className="bg-white border-gray-300 text-gray-900 uppercase tracking-wider font-mono text-lg h-12"
                    data-testid="vin-input"
                  />
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-gray-400">
                      {vin.length}/17 characters
                    </span>
                    {vin.length === 17 && (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filter by Category
                  </label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    className="w-full bg-white border border-gray-300 text-gray-900 rounded-md px-3 py-2"
                  >
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                
                <Button 
                  type="submit" 
                  disabled={loading || vin.length !== 17}
                  className="w-full bg-primary hover:bg-primary/90 h-12 text-lg font-bold uppercase"
                  data-testid="vin-search-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5 mr-2" />
                      Find Parts
                    </>
                  )}
                </Button>
              </form>
              
              {error && (
                <div className="mt-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}
              
              {vehicle && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg" data-testid="vehicle-result">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-800">Vehicle Identified</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {vehicle.year} {vehicle.make} {vehicle.model}
                  </h3>
                  {vehicle.engine && (
                    <p className="text-gray-600 text-sm mt-1">Engine: {vehicle.engine}</p>
                  )}
                  <p className="text-gray-400 text-xs mt-2 font-mono">VIN: {vehicle.vin}</p>
                  
                  <Button 
                    variant="outline"
                    onClick={handleSaveVehicle}
                    disabled={saving}
                    className="w-full mt-4 border-gray-300"
                    data-testid="save-vehicle-btn"
                  >
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save to My Garage
                  </Button>
                </div>
              )}

              {/* VIN Help */}
              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Where to find your VIN:</h4>
                <ul className="text-blue-800 text-sm space-y-1">
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    Driver's side dashboard (visible through windshield)
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    Driver's door jamb sticker
                  </li>
                  <li className="flex items-start gap-2">
                    <ChevronRight className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    Vehicle registration or insurance documents
                  </li>
                </ul>
              </div>

              {/* API Info */}
              <p className="text-xs text-gray-400 mt-4 text-center">
                VIN decoding powered by NHTSA vPIC API
              </p>
            </div>
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-2">
            {!vehicle && !loading && (
              <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                <Car className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h2 className="text-2xl font-heading font-bold text-gray-900 uppercase mb-2">
                  Enter Your VIN
                </h2>
                <p className="text-gray-500 max-w-md mx-auto">
                  Enter your vehicle's VIN number to see all compatible parts. 
                  We'll decode the VIN and show you parts that fit your exact vehicle.
                </p>
              </div>
            )}

            {loading && !vehicle && (
              <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                <Loader2 className="w-12 h-12 text-primary mx-auto mb-4 animate-spin" />
                <p className="text-gray-500">Decoding VIN and searching for compatible parts...</p>
              </div>
            )}

            {vehicle && (
              <>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="font-heading text-2xl font-bold text-gray-900 uppercase">
                      Compatible Parts
                    </h2>
                    <p className="text-gray-500">
                      {products.length} parts found for {vehicle.year} {vehicle.make} {vehicle.model}
                    </p>
                  </div>
                  <Link to={`/shop?year=${vehicle.year}&make=${encodeURIComponent(vehicle.make)}&model=${encodeURIComponent(vehicle.model)}`}>
                    <Button variant="outline" className="border-gray-300">
                      View in Shop
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>

                {products.length === 0 ? (
                  <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
                    <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-xl text-gray-900 mb-2">No specific parts found</h3>
                    <p className="text-gray-500 mb-4">
                      We couldn't find parts specifically matched to your vehicle. 
                      Try browsing our full catalog.
                    </p>
                    <Link to="/shop">
                      <Button className="bg-primary hover:bg-primary/90">
                        Browse All Parts
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {products.map((product) => (
                      <ProductCard key={product.product_id} product={product} />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VINSearchPage;
